<template>
  <Teleport to="body">
    <Transition name="modal-backdrop" appear>
      <div 
        v-if="visible"
        class="modal-backdrop"
        @click="handleBackdropClick"
      >
        <Transition name="modal-slide" appear>
          <div 
            v-if="visible"
            class="modal-container"
            @click.stop
          >
            <div class="modal-header">
              <h2 v-if="title" class="modal-title">{{ title }}</h2>
              <button 
                v-if="showCloseButton" 
                class="modal-close"
                @click="$emit('close')"
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            
            <div class="modal-content">
              <component 
                v-if="component"
                :is="component"
                v-bind="componentProps"
                @close="$emit('close')"
              />
              <div v-else-if="content" v-html="content"></div>
              <slot v-else />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

interface Props {
  visible?: boolean
  title?: string
  content?: string
  component?: any
  componentProps?: Record<string, any>
  showCloseButton?: boolean
  persistent?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  visible: true,
  showCloseButton: true,
  persistent: false
})

const emit = defineEmits<{
  close: []
}>()

const handleBackdropClick = () => {
  if (!props.persistent) {
    emit('close')
  }
}

const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && !props.persistent) {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscKey)
  document.body.style.overflow = 'hidden'
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey)
  document.body.style.overflow = ''
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/mixins.scss';

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  background: $neutral-300;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  min-height: 60px;
}

.modal-title {
  @include h2;
  color: $neutral-100;
  margin: 0;
  text-align: center;
  flex: 1;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: $neutral-60;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  transition: color 0.2s ease;

  &:hover {
    color: $neutral-100;
  }
}

.modal-content {
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.modal-content :deep(*) {
  max-width: 100%;
  box-sizing: border-box;
}

.modal-content :deep(h1),
.modal-content :deep(h2),
.modal-content :deep(p),
.modal-content :deep(li),
.modal-content :deep(a),
.modal-content :deep(span),
.modal-content :deep(strong),
.modal-content :deep(em),
.modal-content :deep(u),
.modal-content :deep(s) {
  color: $neutral-100;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.modal-content :deep(ul) {
  max-width: 100%;
  padding-left: 20px;
}

.modal-content :deep(a) {
  color: $primary-100;
}

.modal-content :deep(.privacy-text-primary) {
  color: $primary-100;
}

.modal-content :deep(.privacy-text-secondary) {
  color: $neutral-60;
}

.modal-content :deep(.privacy-text-default) {
  color: $neutral-100;
}

// Анимации
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: transform 0.3s ease-out;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: translateY(100%);
}

@media (max-width: 768px) {
  .modal-backdrop {
    padding: 0;
  }
  
  .modal-container {
    max-height: 95vh;
    border-radius: 20px 20px 0 0;
  }
  
  .modal-header {
    padding: 20px 20px 0 20px;
  }
  
  .modal-content {
    padding: 20px;
  }
}
</style>
